import {EList, type Expression} from '@packages/systems/wfdl-ast';
import {Expressions, TypeImplementation} from '@packages/systems/wfdl';
import {deleteAt, moveElement} from '@packages/utilities/array';

export type ExpressionListItemAction = Extract<
  TypeImplementation.ExpressionEditorAction,
  {
    type:
      | 'EXPRESSION_LIST_ITEM_ADDED'
      | 'EXPRESSION_LIST_ITEM_REMOVED'
      | 'EXPRESSION_LIST_ITEM_MOVED';
  }
>;

export const isListItemAction = (
  action: TypeImplementation.ExpressionEditorAction
): action is ExpressionListItemAction =>
  action.type === 'EXPRESSION_LIST_ITEM_ADDED' ||
  action.type === 'EXPRESSION_LIST_ITEM_REMOVED' ||
  action.type === 'EXPRESSION_LIST_ITEM_MOVED';

/**
 * Apply a list-item action to a list expression.
 * Returns undefined if the action can not be applied.
 */
export const applyListItemAction = (
  listExpression: Expression,
  action: ExpressionListItemAction
): Expression | undefined => {
  const items = Expressions.getList(listExpression);
  if (items == null) return;
  switch (action.type) {
    case 'EXPRESSION_LIST_ITEM_ADDED':
      return EList([...items, action.payload.item]);
    case 'EXPRESSION_LIST_ITEM_REMOVED': {
      const {index} = action.payload;
      if (index < 0 || index >= items.length) return;
      return EList(deleteAt(items, index));
    }
    case 'EXPRESSION_LIST_ITEM_MOVED': {
      const {fromIndex, toIndex} = action.payload;
      if (
        fromIndex < 0 ||
        fromIndex >= items.length ||
        toIndex < 0 ||
        toIndex >= items.length
      ) {
        return;
      }
      return EList(moveElement(items, fromIndex, toIndex));
    }
  }
};
